import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  ConfirmDialog,
  Alert,
  Paper,
  Text,
  MobileListRow,
  DEFAULT_PAGE_LIMIT,
  formatAddress,
  type Address,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useListVendorsQuery,
  useAddUpdateVendorMutation,
  useDeleteVendorMutation,
} from '../api/tenantApi';
import {
  VendorForm,
  EMPTY_VENDOR_FORM,
  type VendorFormValues,
} from '../features/vendors/VendorForm';
import { useAccess } from '../hooks/useAccess';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';

type VendorRow = {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  address?: Address | null;
  approvalStatus?: string | null;
};

function vendorListTitle(row: VendorRow): string {
  const snippet =
    row.address?.line1?.trim() ||
    row.address?.city?.trim() ||
    formatAddress(row.address);
  return snippet ? `${row.name} (${snippet})` : row.name;
}

export function VendorsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canMutate } = useAccess();
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: DEFAULT_PAGE_LIMIT,
    search: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<VendorRow | null>(null);
  const [form, setForm] = useState<VendorFormValues>(EMPTY_VENDOR_FORM);
  const [confirmDelete, setConfirmDelete] = useState<VendorRow | null>(null);

  const { data, isLoading, isFetching, isError } = useListVendorsQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const [save, { isLoading: saving }] = useAddUpdateVendorMutation();
  const [remove, { isLoading: deleting }] = useDeleteVendorMutation();

  const list = data?.listVendors
    ? {
        rows: (data.listVendors.rows ?? []) as VendorRow[],
        total: data.listVendors.total ?? 0,
        page: query.page,
        pageLimit: query.pageLimit,
      }
    : undefined;

  async function onSave() {
    await save({ ...form, id: editing?.id }).unwrap();
    setModalOpen(false);
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    await remove(confirmDelete.id).unwrap();
    setConfirmDelete(null);
    setModalOpen(false);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={t('vendors.title')}
        description={t('vendors.description')}
      />
      {isError ? <Alert color="red" title={t('vendors.loadFailed')} /> : null}
      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('vendors.searchPlaceholder')}
        emptyTitle={t('vendors.emptyTitle')}
        emptyDescription={t('vendors.emptyDescription')}
        testId="vendor-table"
        mobileView="list"
        addButton={
          canMutate() ? (
            <Button
              testId="vendor-add"
              onClick={() => {
                setEditing(null);
                setForm(EMPTY_VENDOR_FORM);
                setModalOpen(true);
              }}
            >
              {t('vendors.add')}
            </Button>
          ) : undefined
        }
        onRowClick={(row) => navigate(`/vendors/${row.id}`)}
        columns={[
          { key: 'name', header: t('common.name'), render: (row) => row.name },
          { key: 'phone', header: t('common.phone'), render: (row) => row.phone || t('common.emptyValue') },
          { key: 'email', header: t('common.email'), render: (row) => row.email || t('common.emptyValue') },
          {
            key: 'approval',
            header: t('approval.status'),
            render: (row) => <ApprovalStatusBadge status={row.approvalStatus} />,
          },
        ]}
        renderCard={(row: VendorRow) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text fw={600}>{row.name}</Text>
                <ApprovalStatusBadge status={row.approvalStatus} />
              </Group>
              <Text size="sm" c="dimmed">
                {row.phone || t('common.noPhone')} · {row.email || t('common.noEmail')}
              </Text>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: VendorRow) => (
          <MobileListRow
            title={vendorListTitle(row)}
            subtitle={row.phone || t('common.noPhone')}
            onClick={() => navigate(`/vendors/${row.id}`)}
          />
        )}
      />
      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('vendors.edit') : t('vendors.add')}
      >
        <Stack>
          <VendorForm value={form} onChange={setForm} />
          <Group justify="space-between">
            {editing ? (
              <Button color="red" variant="light" onClick={() => setConfirmDelete(editing)}>
                {t('common.delete')}
              </Button>
            ) : (
              <span />
            )}
            <Button loading={saving} onClick={() => void onSave()}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void handleDelete()}
        title={t('vendors.deleteConfirm')}
        message={t('common.deleteMessage', { name: confirmDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </Stack>
  );
}
